import React from 'react';
import PropTypes from 'prop-types';
import { GlobalState, Select } from '@leverege/ui-redux';
import { ValuesCache } from '@leverege/value-cache';
import { ResourceTemporaryUserReducer, AllowedReducer, ApiProvider, Auth } from '@leverege/api-redux';
import { Dialogs, Table, List, Content } from '@leverege/ui-elements';
import { Toolbar, TitleBar, createConnect } from '@leverege/ui-plugin';
import I18N from '@leverege/i18n';
import { getColumns } from "./ResourceTemporaryUsersColumns.js";
import Paginator from "../Paginator.js";
import TablePagination from "../TablePagination.js";
import Icons from "../Icons.js";
import S from './ResourceTemporaryUsersScreen.css';
import { getUiNameCalculator, getUiUrlCalculator } from "./Util.js";
import NoDataView from "./components/NoDataView.js"; // import ResendPasswordLoading from './user/ResendPasswordLoading'
const {
  t
} = I18N.ns('temporaryUserManagement.ResourceTemporaryUsersScreen');
class ResourceTemporaryUsersScreen extends React.Component {
  static CLIENT = 'ResourceTemporaryUsers';
  static ACTION_MATCH = {
    use: 'actionBar',
    client: ResourceTemporaryUsersScreen.CLIENT
  };
  static CONTEXT_MATCH = {
    use: 'contextMenu',
    client: ResourceTemporaryUsersScreen.CLIENT
  };
  static TABLE_POST_COLUMN_MATCH = {
    use: 'postActionsColumnBar',
    client: ResourceTemporaryUsersScreen.CLIENT
  };
  static TABLE_FOOTER_MATCH = {
    use: 'actionsFooterBar',
    client: ResourceTemporaryUsersScreen.CLIENT
  };
  static propTypes = {
    users: PropTypes.array,
    title: PropTypes.string,
    showHeader: PropTypes.bool,
    uiName: PropTypes.string,
    calcUiName: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    calcUiUrl: PropTypes.oneOfType([PropTypes.bool, PropTypes.func])
  };
  static defaultProps = {
    users: [],
    get title() {
      return t('title');
    },
    showHeader: true,
    uiName: null,
    calcUiName: true,
    calcUiUrl: true
  };
  static contextType = ApiProvider.Context;
  constructor(props) {
    super(props);
    this.cache = new ValuesCache();
    this.state = {
      resent: {}
    };
    this.paginator = new Paginator({
      onPageChange: this.onSetPage
    });
  }
  getContext(optionalUser) {
    const {
      targeted,
      actions,
      resourceId,
      api,
      emailOptions,
      smsOptions,
      uiName,
      calcUiName,
      systemId,
      uiUrl,
      calcUiUrl
    } = this.props;
    const context = {
      target: optionalUser || targeted,
      targetType: 'temporaryUser',
      client: 'ResourceTemporaryUsers',
      resourceId,
      actions,
      api,
      emailOptions,
      smsOptions,
      // if calcUiName is just true, use the default, but if its a function use it to calc ui name
      uiName: calcUiName === true ? getUiNameCalculator(uiName) : typeof calcUiName === 'function' ? calcUiName : uiName,
      uiUrl: calcUiUrl === true ? getUiUrlCalculator(uiUrl) : typeof calcUiUrl === 'function' ? calcUiUrl : uiUrl,
      clientProps: this.props,
      systemId
    };
    return context;
  }
  componentDidMount() {
    window.GlobalState = GlobalState;
    const {
      dispatch,
      actions,
      usersState
    } = this.props;
    dispatch(Select.clear('resourceTemporaryUsersTargeted'));
    if (ResourceTemporaryUserReducer.needsLoading(usersState, undefined, 100)) {
      dispatch(actions.temporaryUsers().list({
        offset: 0,
        limit: 100,
        order: ['userId']
      }));
    }
  }
  componentWillUnmount() {
    this.props.dispatch(Select.clear('resourceTemporaryUsersTargeted')); // eslint-disable-line react/destructuring-assignment
  }
  onSetPage = ({
    page,
    perPage
  }) => {
    const {
      dispatch,
      actions
    } = this.props;
    dispatch(actions.temporaryUsers().list({
      offset: page * perPage,
      limit: perPage,
      order: ['userId']
    }));
  };
  onCheckAll = () => {
    const {
      targeted,
      users,
      dispatch
    } = this.props;
    if (Select.isEmpty(targeted)) {
      const ids = users.map(e => e.username);
      dispatch(Select.set(ids, 'resourceTemporaryUsersTargeted'));
    } else {
      dispatch(Select.clear('resourceTemporaryUsersTargeted'));
    }
  };
  onCheck = ({
    data
  }) => {
    const {
      dispatch
    } = this.props;
    dispatch(Select.toggle(data.username, 'resourceTemporaryUsersTargeted'));
  };
  createDataItems = (items, offset, limit, count, status) => {
    return {
      status: status || null,
      loading: status === 'loading',
      page: Math.ceil(offset / limit) || 0,
      perPage: limit || 100,
      count: count || 0,
      items: items || []
    };
  };
  render() {
    const {
      resent
    } = this.state;
    const {
      users,
      offset,
      limit,
      count,
      status,
      targeted,
      targetedCount,
      title,
      showHeader,
      variants,
      noDataMessage = t('noDataText')
    } = this.props;
    const postActionsToolbar = /*#__PURE__*/React.createElement(Toolbar, {
      variant: variants?.toolbar || 'actionBar',
      prefer: "icon",
      matchContext: ResourceTemporaryUsersScreen.TABLE_POST_COLUMN_MATCH,
      context: this.getContext()
    });
    const columns = getColumns({
      context: this.getContext(),
      targeted,
      targetedCount,
      users,
      postActionsToolbar,
      onCheck: this.onCheck,
      onCheckAll: this.onCheckAll,
      onResend: this.onResend,
      resent
    });
    const dataItems = this.cache.calc('items', this.createDataItems, users, offset, limit, count, status);
    this.paginator.setData(dataItems);
    const noData = /*#__PURE__*/React.createElement(NoDataView, {
      label: noDataMessage
    });
    return /*#__PURE__*/React.createElement(Content, null, /*#__PURE__*/React.createElement(Content.Area, null, /*#__PURE__*/React.createElement(Content, null, showHeader ? /*#__PURE__*/React.createElement(Content.Header, null, /*#__PURE__*/React.createElement(TitleBar, {
      icon: Icons.temporaryUserScreenIcon,
      title: title,
      variant: "screenTitle"
    }, /*#__PURE__*/React.createElement(Toolbar, {
      variant: variants?.toolbar || 'actionBar',
      prefer: "icon",
      hasContextMenu: true,
      contextMenuHideWhenEmpty: false,
      matchContext: ResourceTemporaryUsersScreen.ACTION_MATCH,
      context: this.getContext()
    }))) : null, /*#__PURE__*/React.createElement(Content.Area, null, /*#__PURE__*/React.createElement(Table, {
      variant: variants?.table,
      className: dataItems?.count > 0 ? null : S.table,
      manual: true,
      sortable: true,
      pageSize: Math.min(limit, users.length),
      showPagination: false,
      showPageSizeOptions: false,
      showPaginationBottom: false,
      columns: columns,
      data: this.paginator.pageItems,
      loading: this.paginator.loading,
      noDataText: noData
    })), /*#__PURE__*/React.createElement(Content.Footer, null, /*#__PURE__*/React.createElement(TablePagination, {
      context: this.getContext(),
      matchContext: ResourceTemporaryUsersScreen.TABLE_FOOTER_MATCH,
      paginator: this.paginator
    })))));
  }
}
const connect = createConnect(ResourceTemporaryUsersScreen.CLIENT, {
  client: ResourceTemporaryUsersScreen.CLIENT
});
export default connect((state, props) => {
  const {
    resourceId
  } = props;
  return {
    allowed: AllowedReducer.get(state, resourceId),
    usersState: ResourceTemporaryUserReducer.get(state, resourceId),
    users: ResourceTemporaryUserReducer.getTemporaryUsers(state, resourceId),
    count: ResourceTemporaryUserReducer.getTemporaryUserCount(state, resourceId),
    limit: ResourceTemporaryUserReducer.getTemporaryUserLimit(state, resourceId),
    offset: ResourceTemporaryUserReducer.getTemporaryUserOffset(state, resourceId),
    status: ResourceTemporaryUserReducer.getStatus(state, resourceId),
    error: ResourceTemporaryUserReducer.getError(state, resourceId),
    targeted: Select.get(state.select, 'resourceTemporaryUsersTargeted'),
    targetedCount: Select.count(state.select, 'resourceTemporaryUsersTargeted'),
    fullAllowed: state.allowed
  };
}, null, null, {
  forwardRef: true
})(ResourceTemporaryUsersScreen);